import HBList from "@/components/HBList";
import Image from "@/components/Image";
import { useLogin } from "@/components/LoginModal";
import { PIN_PRIVATE_STATUS } from "@/constants/pin";
import useAppStore from "@/context/store";
import { getBoardPins } from "@/services";
import { getContentType } from "@/utils/getContentType";
import { getImageSrc2x } from "@/utils/imageUrl";
import useRequest from "@/utils/useReq";
import { useMemoizedFn, useSetState, useUpdateEffect } from "ahooks";
import classNames from "classnames";
import { last } from "lodash";
import { useCallback, useRef, useState } from "react";
import { Violation } from "../Violation";
import { useIsViolationAb } from "../Violation/useIsViolationAb";
import styles from "./index.module.less";

const masonryOptions = {
  threshold: 200,
  gutter: 2,
  columnSize: 82.5,
  brickId: "pin_id",
};

const scrollableTarget = "pinBoardPinList";

const infiniteScrollOptions = {
  endMessage: null,
  loader: null,
};

export default function PinBoardPinList({ pin, onSelect }) {
  const user = useAppStore((s) => s.user);
  const { login } = useLogin();
  const pinId = pin?.pin_id;
  const boardId = pin?.board_id;
  const [query, setQuery] = useSetState({
    max: last(pin?.board?.pins)?.pin_id || 0,
    boardId: boardId,
  });
  const [hasMore, setHasMore] = useState(true);
  const [dataSource, setDataSource] = useState(pin?.board?.pins || []);

  const initRef = useRef(pin?.board?.pins);

  useRequest(
    async () => {
      const { max, boardId } = query;

      if (!boardId) {
        return;
      }

      // 当有初始数据时，不做请求，避免没有必要的请求
      if (initRef.current) {
        initRef.current = false;
        return;
      }

      const result = await getBoardPins({
        board_id: boardId,
        limit: 20,
        ...(max ? { max } : null),
      });
      const pins = result?.pins || [];

      if (pins.length <= 0) {
        setHasMore(false);
      }

      setDataSource(dataSource.concat(pins));
    },
    {
      refreshDeps: [query],
    }
  );

  // 采集变化触发更新
  useUpdateEffect(() => {
    const pins = pin?.board?.pins || [];
    if (boardId) {
      setHasMore(true);
      setDataSource(pins);
      const last = pins[pins.length - 1];
      if (last) {
        setQuery({
          boardId,
          max: last.seq || last.pin_id,
        });
      }
    }
  }, [boardId, pin?.board?.pins, setQuery]);

  const onLoadMore = useMemoizedFn(() => {
    const last = dataSource[dataSource.length - 1];
    setQuery({
      boardId,
      max: last.seq || last.pin_id,
    });
  });

  const isViolationAb = useIsViolationAb();

  const render = useCallback(
    (pin) => {
      const height = Math.min(
        260,
        Math.round((pin.file?.height / pin.file?.width) * 82.5)
      );

      const onClick = async (evt) => {
        if (evt.metaKey || evt.ctrlKey) {
          return;
        }

        evt.preventDefault();
        if (!user) {
          login();
        }

        const currentSrc = evt.currentTarget.querySelector("img").currentSrc;

        onSelect(pin, currentSrc, "sameBoard");
      };

      const onImgLoad = (evt) => {
        const target = evt.target;
        target?.classList.add("transparent-img-bg");
      };

      if (
        pin.is_private === PIN_PRIVATE_STATUS.VIOLATION &&
        isViolationAb.new
      ) {
        return <Violation hideText style={{ width: 82.5, height: 82.5 }} />;
      }

      return (
        <a
          className={classNames([
            styles.item,
            pinId === pin?.pin_id ? styles.active : null,
          ])}
          style={{ height }}
          key={pin.pin_id}
          href={`/pins/${pin.pin_id}`}
          onClick={onClick}
          data-gd-expose="content_expose"
          data-content-type={getContentType(pin?.extra)}
          data-gd-click="material_click"
          data-content-id={pin.pin_id}
          data-material-id={pin.file_material?.material_id}
          data-board-id={boardId}
          data-file-id={pin.file_id}
          data-ext={JSON.stringify(pin?.ext)}
        >
          <div className={styles.img}>
            <Image
              onLoad={onImgLoad}
              {...getImageSrc2x(pin.file, { width: 82.5 })}
              width={82.5}
              height={Math.round((pin.file?.height / pin.file?.width) * 82.5)}
              alt={pin.raw_text}
              showError={true}
            />
          </div>
        </a>
      );
    },
    [pinId, boardId, user, onSelect, login, isViolationAb.new]
  );

  return (
    <div
      className={styles.preview}
      id={scrollableTarget}
      data-content-source="同画板"
    >
      <HBList
        dataSource={dataSource}
        uniqueKey={"pin_id"}
        scrollableTarget={scrollableTarget}
        render={render}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        masonryOptions={masonryOptions}
        infiniteScrollOptions={infiniteScrollOptions}
        scrollThreshold={"100px"}
      />
    </div>
  );
}
